import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton} from "@mui/material";
import {Close, Refresh} from "@mui/icons-material";
import {useSitStores} from "~/stores";
import type {AuditLogEntry} from "../ApiTypes";
import {AuditActionLabel, SitAuditModelLabel} from "../ApiTypes";

const displayValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
};

// ADMIN-only: fetches and shows the actual before/after values for one audit entry.
// See sit_audit.views.SitAuditLogVS.retrieve — everyone else gets a 403 here.
export const AuditLogDetailModal: React.FC<{entry: AuditLogEntry | null; onClose: () => void}> = observer(
    ({entry, onClose}) => {
        const {auditLogStore} = useSitStores();
        const rm = auditLogStore.auditLogDetailRM;

        useEffect(() => {
            if (entry) rm.request({id: entry.id});
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [entry?.id]);

        if (!entry) return null;

        const detail = rm.result?.id === entry.id ? rm.result : undefined;
        const changes = detail?.changes;
        const fields = changes
            ? Array.from(new Set([...Object.keys(changes.before ?? {}), ...Object.keys(changes.after ?? {})]))
            : [];

        return (
            <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {SitAuditModelLabel[entry.target_model]} — {AuditActionLabel[entry.action]}
                    <IconButton onClick={onClose} size="small" style={{position: 'absolute', right: 8, top: 8}}
                                aria-label="Close">
                        <Close fontSize="small"/>
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <div className="sit-muted" style={{marginBottom: 12}}>
                        {new Date(entry.created_at).toLocaleString()} · {entry.actor?.name ?? 'System'}
                    </div>
                    {rm.busy &&
                        <div className="sit-muted" style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <CircularProgress size={16}/> Loading…
                        </div>}
                    {rm.error && !rm.busy &&
                        <div className="sit-muted" style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            Failed to load audit details.
                            <Button size="small" startIcon={<Refresh fontSize="small"/>}
                                    onClick={() => rm.request({id: entry.id})}>
                                Retry
                            </Button>
                        </div>}
                    {detail && fields.length === 0 &&
                        <div className="sit-muted">No field changes recorded for this event.</div>}
                    {detail && fields.length > 0 &&
                        <div className="sit-table-wrap">
                            <table className="sit-table">
                                <thead>
                                    <tr><th>Field</th><th>Before</th><th>After</th></tr>
                                </thead>
                                <tbody>
                                    {fields.map((field) => (
                                        <tr key={field}>
                                            <td>{field}</td>
                                            <td>{displayValue(changes!.before?.[field])}</td>
                                            <td>{displayValue(changes!.after?.[field])}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>}
                </DialogContent>
            </Dialog>
        );
    });
